import type { Expression, Filter } from '@fabric-ui/components';
import { isExpr } from './filter-utils';

/**
 *
 * @param source - The source Filter
 * @param predicate - Predicate function to decide which leaves to remove
 * @returns The filtered Filter or null if all leaves were removed
 *
 * @example
 * ```ts
 * filterLeaves(filter, leaf => leaf.expression.fieldName === 'a');
 * ```
 */
export const filterLeaves = (source: Filter, predicate: (leaf: { expression: Expression }) => boolean): Filter | null => {
  if (isExpr(source)) {
    return predicate(source) ? null : source;
  }

  const op = 'and' in source ? 'and' : 'or';
  const kids: Filter[] = source[op]?.operands || [];

  const { newKids, changed } = kids.reduce(
    (acc, child) => {
      const res = filterLeaves(child, predicate);

      if (res === null) {
        return { newKids: acc.newKids, changed: true };
      }

      return {
        newKids: [...acc.newKids, res],
        changed: acc.changed || res !== child,
      };
    },
    { newKids: [] as Filter[], changed: false }
  );

  if (!changed) {
    return source;
  }

  return newKids.length === 0 ? null : newKids.length === 1 ? newKids[0] : ({ [op]: { operands: newKids } } as Filter);
};
